// frontend/src/services/adjudicationService.js
// My queue and the adjudication item (YES-255–258), on the built routes of docs/openapi.yaml
// (call map sections 4–6). It turns the API's answers into the step the screen shows: blind,
// reveal, tie or done. Every answer is built for the adjudicator by the endpoint: utterance text and
// speaker role, their own codes, earlier human labels after lock, and for a tie-breaker the two blind
// codes. Never model or LLM output, flags, selection reasons, stages or queue positions (hard
// constraint). Item IDs are opaque public IDs and travel only in API paths.
import api from './api';

const enc = encodeURIComponent;
const DIMS = ['ut', 'rc'];

// The run's codebook and reason codes, fetched once per run
const codebooks = new Map();

export const getCodebook = async (runId) => {
    if (!codebooks.has(runId)) {
        const request = api.get(`/api/queue/${enc(runId)}/codebook`).then(({ data }) => ({
            version: data.version,
            ut: data.ut,
            rc: data.rc,
            reasonCodes: data.reasonCodes,
        }));
        codebooks.set(runId, request);
        request.catch(() => codebooks.delete(runId));
    }
    return codebooks.get(runId);
};

export const getQueue = async () => (await api.get('/api/queue')).data.cards;

// ContextLine[] → the five slots of the ±2 window, null where the lesson starts or ends
const toItem = (item) => {
    const target = item.window.find((line) => line.target) || item.window[Math.floor(item.window.length / 2)];
    const bySeq = new Map(item.window.map((line) => [line.seq, line]));
    const window = [-2, -1, 0, 1, 2].map((offset) => {
        const line = bySeq.get(target.seq + offset);
        return line ? { offset, number: line.seq, speaker: line.speakerRole, text: line.text } : null;
    });
    return { id: item.id, speaker: target.speakerRole, window };
};

// Reveal → the reveal step; reason codes only for the dimensions that differ
const toReveal = (reveal, codebook) => ({
    step: 'reveal',
    yours: reveal.yours,
    earlier: reveal.earlier,
    reasonsNeeded: reveal.differs,
    reasonCodes: Object.fromEntries(DIMS.filter((d) => reveal.differs[d]).map((d) => [d, codebook.reasonCodes[d]])),
});

const reasonLabel = (codebook, dim, code) => codebook.reasonCodes[dim].find((r) => r.code === code)?.label || code;

// The caller's next item, at the step where they stopped (GET /api/queue/{runId}/next)
export const getNext = async (runId) => {
    const { data } = await api.get(`/api/queue/${enc(runId)}/next`);
    const base = { run: { id: runId, name: data.run.name }, progress: data.progress };
    const { item } = data;
    if (!item) return { ...base, step: 'done' };

    const codebook = await getCodebook(runId);
    if (item.kind === 'tie_break') {
        return {
            ...base,
            step: 'tie',
            item: toItem(item),
            tiedDimensions: item.tiedDimensions,
            agreed: item.agreed || {},
            codes: item.codes.map((code, i) => ({
                label: `Code ${i + 1}`,
                ut: code.ut,
                rc: code.rc,
                unsure: code.unsure,
                reasons: Object.fromEntries(DIMS.filter((d) => code.reasonCodes?.[d])
                    .map((d) => [d, { code: code.reasonCodes[d], label: reasonLabel(codebook, d, code.reasonCodes[d]) }])),
                note: code.note || null,
            })),
            earlier: item.earlier,
            earlierKind: item.earlierSource,
        };
    }
    if (item.state === 'locked') {
        // Locked earlier, reason still due: the reveal again (GET /api/items/{itemId}/reveal)
        const { data: reveal } = await api.get(`/api/items/${enc(item.id)}/reveal`);
        return { ...base, item: toItem(item), ...toReveal(reveal, codebook), reopened: true };
    }
    return { ...base, step: 'blind', item: toItem(item) };
};

export const getWholeLesson = async (runId, itemId) => {
    const { data } = await api.get(`/api/items/${enc(itemId)}/lesson`);
    return data.lines.map((line) => ({ number: line.seq, speaker: line.speakerRole, text: line.text, target: Boolean(line.target) }));
};

// Answers with the reveal step (yours, earlier, the dimensions that differ, their reason codes)
export const lockCode = async (runId, itemId, { ut, rc, unsure }) => {
    const { data } = await api.post(`/api/items/${enc(itemId)}/lock`, { ut, rc, unsure: Boolean(unsure) });
    return { ...toReveal(data, await getCodebook(runId)), reopened: false };
};

export const giveReasons = async (runId, itemId, { reasons, note }) => {
    const body = {};
    if (reasons && Object.keys(reasons).length) body.reasonCodes = reasons;
    if (note) body.note = note;
    return (await api.post(`/api/items/${enc(itemId)}/done`, body)).data;
};

export const finishTieBreak = async (runId, itemId, { codes, note }) => {
    const body = { ...codes };
    if (note) body.note = note;
    return (await api.post(`/api/items/${enc(itemId)}/tie-break`, body)).data;
};
